/**
 * Блок «с чего начать» на странице курса.
 * apps/web/src/components/CourseDiagnostic.tsx
 *
 * Заменяет собой первое впечатление от курса: вместо оглавления на 27 пунктов
 * человек проходит шесть ситуаций и получает три темы, с которых начать именно ему.
 * Разбор показывается только тем, кто ещё не начинал курс, и только там, где он есть.
 */

import { useEffect, useState } from 'react';
import { getAuthHeaders } from '../lib/auth-headers';

/** Разбор курса приходит с сервера: это данные, а не часть сайта. */
interface DiagnosticOption { text: string; correct?: boolean }
interface DiagnosticQuestion {
  id: string;
  situation: string;
  options: DiagnosticOption[];
  modules: string[];
  explain: string;
}
interface CourseDiagnosticData {
  title: string;
  intro: string;
  questions: DiagnosticQuestion[];
  advancedModules: string[];
  advancedNote: string;
}

interface Module {
  id: string;
  name: string;
}
interface Section {
  id: string;
  name: string;
  modules: Module[];
}

interface Props {
  collectionId: string;
  sections: Section[];
  onModuleStart: (moduleId: string) => void;
  /** Человек уже начал курс — тогда не занимаем экран, показываем одну строку. */
  started?: boolean;
  /** Странице нужно знать, есть ли разбор: без него не на что вести ссылкой. */
  onAvailability?: (has: boolean) => void;
  /** Странице нужны рекомендованные темы: подсветить и вести «Продолжить». */
  onResult?: (modules: string[] | null) => void;
}

/** Ответ засчитан — что показать и куда вести. */
interface Answered {
  correct: boolean;
  modules: string[];
}

const storageKey = (id: string) => `beshek:diagnostic:${id}`;

export default function CourseDiagnostic({ collectionId, sections, onModuleStart, started: alreadyStarted, onAvailability, onResult }: Props) {
  const [diagnostic, setDiagnostic] = useState<CourseDiagnosticData | null>(null);

  useEffect(() => {
    let alive = true;
    fetch(`/api/collections/${collectionId}/diagnostic`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        const has = Boolean(d?.questions?.length);
        if (has) setDiagnostic(d);
        onAvailability?.(has);
      })
      .catch(() => {
        /* разбора у курса нет — блока просто не будет */
        onAvailability?.(false);
      });
    return () => {
      alive = false;
    };
  }, [collectionId]);

  const saved = (() => {
    try {
      const raw = localStorage.getItem(storageKey(collectionId));
      return raw ? (JSON.parse(raw) as { modules: string[] }) : null;
    } catch {
      return null;
    }
  })();

  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [chosen, setChosen] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Answered[]>([]);
  const [result, setResult] = useState<string[] | null>(saved?.modules ?? null);

  // Результат живёт на сервере (формат v2): с него курс ведёт человека; браузерная копия — запасной вариант
  useEffect(() => {
    let alive = true;
    fetch(`/api/collections/${collectionId}/diagnostic/result`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive && Array.isArray(d?.modules) && d.modules.length) setResult(d.modules);
      })
      .catch(() => { /* сервер не ответил — останется браузерная копия */ });
    return () => { alive = false; };
  }, [collectionId]);
  useEffect(() => { onResult?.(result); }, [result]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!diagnostic) return null;

  const moduleName = (moduleId: string): string => {
    for (const section of sections) {
      const found = (section.modules || []).find((m) => m.id === moduleId);
      if (found) return found.name;
    }
    return moduleId;
  };
  // Порядок курса — он же порядок изучения: не тасуем темы ради «важности».
  const courseOrder = sections.flatMap((s) => (s.modules || []).map((m) => m.id));

  const finish = (all: Answered[]) => {
    const missed = all.filter((a) => !a.correct).flatMap((a) => a.modules);
    const unique = Array.from(new Set(missed));
    const picked = (unique.length > 0 ? unique : diagnostic.advancedModules)
      .sort((a, b) => courseOrder.indexOf(a) - courseOrder.indexOf(b))
      .slice(0, 3);
    setResult(picked);
    try {
      localStorage.setItem(storageKey(collectionId), JSON.stringify({ modules: picked }));
    } catch {
      /* приватный режим браузера — просто не запомним */
    }
    fetch(`/api/collections/${collectionId}/diagnostic/result`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'content-type': 'application/json' },
      body: JSON.stringify({ modules: picked }),
    }).catch(() => { /* не сохранилось на сервере — браузерная копия есть */ });
  };

  const answer = (index: number) => {
    if (chosen !== null) return;
    setChosen(index);
    const question = diagnostic.questions[step];
    setAnswers([
      ...answers,
      { correct: !!question.options[index].correct, modules: question.modules },
    ]);
  };

  const next = () => {
    const all = answers;
    if (step + 1 >= diagnostic.questions.length) {
      finish(all);
      return;
    }
    setStep(step + 1);
    setChosen(null);
  };

  const restart = () => {
    setResult(null);
    setAnswers([]);
    setStep(0);
    setChosen(null);
    setStarted(true);
    try {
      localStorage.removeItem(storageKey(collectionId));
    } catch {
      /* не страшно */
    }
    fetch(`/api/collections/${collectionId}/diagnostic/result`, { method: 'DELETE', headers: getAuthHeaders() }).catch(() => {});
  };

  // ── Итог ──────────────────────────────────────────────────────────────────
  if (result) {
    const knowsEverything = answers.length > 0 && answers.every((a) => a.correct);
    return (
      <div className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900">Вам — с этих тем</h2>
        <p className="text-sm text-gray-500 mt-1">
          {knowsEverything ? diagnostic.advancedNote : 'Остальное можно смотреть потом или не смотреть вовсе.'}
        </p>

        <div className="mt-4 space-y-2">
          {result.map((moduleId, i) => (
            <button
              key={moduleId}
              onClick={() => onModuleStart(moduleId)}
              className="w-full flex items-center gap-3 text-left bg-blue-50 hover:bg-blue-100
                         border border-blue-100 rounded-xl px-4 py-3 transition-colors"
            >
              <span className="w-7 h-7 shrink-0 rounded-full bg-blue-500 text-white text-sm
                               font-semibold flex items-center justify-center">
                {i + 1}
              </span>
              <span className="font-medium text-gray-900">{moduleName(moduleId)}</span>
            </button>
          ))}
        </div>

        <button
          onClick={restart}
          className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          Пройти разбор заново
        </button>
      </div>
    );
  }

  // ── Приглашение ───────────────────────────────────────────────────────────
  if (!started) {
    // Начавшему курс — одна строка, чтобы не перекрывать продолжение
    if (alreadyStarted) {
      return (
        <button
          onClick={() => setStarted(true)}
          className="w-full text-left bg-white rounded-2xl border border-blue-100 px-5 py-3 shadow-sm hover:border-blue-200 transition-colors"
        >
          <span className="font-medium text-gray-900">Не помните, что дальше?</span>{" "}
          <span className="text-blue-600">Пройдите разбор — подскажем три темы</span>
        </button>
      );
    }
    return (
      <div className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm">
        <h2 className="text-lg font-bold text-gray-900">{diagnostic.title}</h2>
        <p className="text-sm text-gray-600 mt-1">{diagnostic.intro}</p>
        <button
          onClick={() => setStarted(true)}
          className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-white font-medium
                     rounded-xl py-3 transition-colors"
        >
          Начать — {diagnostic.questions.length} ситуаций, пара минут
        </button>
      </div>
    );
  }

  // ── Ситуация ──────────────────────────────────────────────────────────────
  const question = diagnostic.questions[step];
  const answered = chosen !== null;

  return (
    <div className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm">
      <div className="flex items-center gap-2 text-xs text-gray-400 uppercase tracking-wide">
        Ситуация {step + 1} из {diagnostic.questions.length}
      </div>
      <p className="mt-2 font-medium text-gray-900">{question.situation}</p>

      <div className="mt-4 space-y-2">
        {question.options.map((option, i) => {
          const isChosen = chosen === i;
          const tone = !answered
            ? 'bg-gray-50 hover:bg-gray-100 border-gray-200'
            : option.correct
              ? 'bg-green-50 border-green-300'
              : isChosen
                ? 'bg-red-50 border-red-200'
                : 'bg-gray-50 border-gray-200 opacity-60';
          return (
            <button
              key={i}
              onClick={() => answer(i)}
              disabled={answered}
              className={`w-full text-left border rounded-xl px-4 py-3 transition-colors ${tone}`}
            >
              <span className="text-gray-900">{option.text}</span>
            </button>
          );
        })}
      </div>

      {answered && (
        <div className="mt-4">
          <p className="text-sm text-gray-600">{question.explain}</p>
          <button
            onClick={next}
            className="mt-3 w-full bg-blue-500 hover:bg-blue-600 text-white font-medium
                       rounded-xl py-3 transition-colors"
          >
            {step + 1 >= diagnostic.questions.length ? 'Показать, с чего начать' : 'Дальше'}
          </button>
        </div>
      )}
    </div>
  );
}
